import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Alert, Box, Button, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router-dom';
import { ResponseError, type Team, type TeamsApi } from 'api-client';
import TeamEditorFields, { type TeamFieldErrors } from './TeamEditorFields';

interface TeamFormProps {
  apiClient: TeamsApi;
  onSave: (team: Team) => void;
  onCancel: () => void;
}

export default function TeamForm({ apiClient, onSave, onCancel }: TeamFormProps) {
  const { teamSlug } = useParams<{ teamSlug: string }>();
  const editing = Boolean(teamSlug);
  const [team, setTeam] = useState<Team | null>(null);
  const [name, setName] = useState<string>('');
  const [slug, setSlug] = useState<string>('');
  const [slugEdited, setSlugEdited] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(editing);
  const [pending, setPending] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<TeamFieldErrors>({});
  const [retry, setRetry] = useState<number>(0);
  const active = useRef<boolean>(true);

  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setTeam(null);
    setName('');
    setSlug('');
    setSlugEdited(false);
    setFieldErrors({});
    setError(null);
    if (!teamSlug) {
      setLoading(false);
      return;
    }
    setLoading(true);
    apiClient
      .teamsList({ slug: teamSlug })
      .then((response) => {
        if (cancelled) return;
        const found = response.results?.find((value) => value.slug === teamSlug);
        if (!found) {
          setError('Team not found or access is unavailable.');
          return;
        }
        setTeam(found);
        setName(found.name);
        setSlug(found.slug || '');
      })
      .catch(() => {
        if (!cancelled) setError('Could not load team details. Please retry.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [apiClient, teamSlug, retry]);

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (pending || loading || (editing && !team?.isAdmin)) return;
    const validation: TeamFieldErrors = {};
    if (!name.trim()) validation.name = ['Enter a team name.'];
    if (!/^[A-Za-z0-9_-]+$/.test(slug.trim())) validation.slug = ['Use letters, numbers, underscores and hyphens.'];
    setFieldErrors(validation);
    if (Object.keys(validation).length) return;
    setPending(true);
    setError(null);
    try {
      const fields = { name: name.trim(), slug: slug.trim() };
      const saved =
        editing && team
          ? await apiClient.teamsUpdate({ id: team.id, team: fields })
          : await apiClient.teamsCreate({ team: fields });
      if (active.current) onSave(saved);
    } catch (failure) {
      if (!active.current) return;
      if (failure instanceof ResponseError && failure.response.status === 400) {
        const body: unknown = await failure.response.json().catch((): null => null);
        if (!active.current) return;
        if (body && typeof body === 'object') {
          const fields: TeamFieldErrors = {};
          for (const key of ['name', 'slug'] as const) {
            const value = Reflect.get(body, key);
            if (Array.isArray(value) && value.every((message) => typeof message === 'string')) fields[key] = value;
          }
          setFieldErrors(fields);
        }
      }
      setError(
        editing
          ? 'Could not update the team. Check the fields and try again.'
          : 'Could not create the team. Check the fields and try again.',
      );
    } finally {
      if (active.current) setPending(false);
    }
  };

  return (
    <Paper sx={{ p: { xs: 2, sm: 3 }, maxWidth: 600, mx: 'auto' }}>
      <Typography component="h1" variant="h5" mb={2}>
        {editing ? 'Team details' : 'Create Team'}
      </Typography>
      {loading ? (
        <Stack direction="row" spacing={2} alignItems="center">
          <CircularProgress aria-label="Loading team details" />
          <Typography>Loading team…</Typography>
        </Stack>
      ) : editing && !team ? (
        <Stack spacing={2}>
          <Alert severity="error">{error}</Alert>
          <Stack direction="row" spacing={2}>
            <Button onClick={() => setRetry((value) => value + 1)}>Retry</Button>
            <Button onClick={onCancel}>Back to teams</Button>
          </Stack>
        </Stack>
      ) : (
        <Box component="form" onSubmit={submit}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {editing && !team?.isAdmin && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Only team administrators can edit these details.
            </Alert>
          )}
          <TeamEditorFields
            name={name}
            slug={slug}
            editing={editing}
            pending={pending}
            readOnly={editing && !team?.isAdmin}
            errors={fieldErrors}
            onCancel={onCancel}
            onName={(value) => {
              setName(value);
              if (!editing && !slugEdited)
                setSlug(
                  value
                    .toLowerCase()
                    .replace(/[^a-z0-9\s_-]/g, '')
                    .trim()
                    .replace(/\s+/g, '-'),
                );
            }}
            onSlug={(value) => {
              setSlug(value);
              setSlugEdited(true);
            }}
          />
        </Box>
      )}
    </Paper>
  );
}
